import type { ReactNode } from 'react';
import { cn } from '@/core/lib/utils';
import { Skeleton } from '@/shared/components/ui/skeleton';

interface KpiCardProps {
  icon: ReactNode;
  label: string;
  value: string;
  sublabel: string;
  variant?: 'default' | 'success' | 'positive' | 'negative';
  isLoading?: boolean;
  'data-testid'?: string;
}

const VARIANT_STYLES: Record<NonNullable<KpiCardProps['variant']>, string> = {
  default: 'bg-card',
  success: 'bg-green-50 dark:bg-green-950/20',
  positive: 'bg-green-50 dark:bg-green-950/20',
  negative: 'bg-red-50 dark:bg-red-950/20',
};

const VALUE_COLOR: Record<NonNullable<KpiCardProps['variant']>, string> = {
  default: 'text-foreground',
  success: 'text-green-700 dark:text-green-400',
  positive: 'text-green-700 dark:text-green-400',
  negative: 'text-red-700 dark:text-red-400',
};

export function KpiCard({
  icon,
  label,
  value,
  sublabel,
  variant = 'default',
  isLoading = false,
  'data-testid': testId,
}: KpiCardProps) {
  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-2 rounded-lg border bg-card p-4">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-7 w-16" />
        <Skeleton className="h-3 w-32" />
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className={cn('flex flex-1 items-start gap-3 rounded-lg border p-4', VARIANT_STYLES[variant])}
    >
      <div className="mt-0.5 flex-shrink-0 text-muted-foreground">{icon}</div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs text-muted-foreground truncate">{label}</span>
        <span className={cn('text-xl font-bold leading-tight', VALUE_COLOR[variant])}>{value}</span>
        <span className="text-xs text-muted-foreground truncate">{sublabel}</span>
      </div>
    </div>
  );
}
